import type { OtpClientCreate, WaInstance } from '@erp/shared';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '../../../components/shadcn/index.js';
import { FormField } from '../../../components/ui/index.js';

/** Mirrors the backend money rule (USD string, up to 4 decimals). */
export const OTP_PRICE_RE = /^\d+(\.\d{1,4})?$/;

/** Light client-side email check; the server does the strict validation. */
export const OTP_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CODE_LENGTHS = [4, 5, 6, 7, 8] as const;

/** String-typed where the operator types (price/ttl) so partial input never fights the state. */
export interface OtpClientFormValues {
  name: string;
  /** Optional contact details — '' = not provided (sent as null). */
  company: string;
  contactEmail: string;
  contactPhone: string;
  /** '' = no instance (unconfigured client). */
  instanceName: string;
  pricePerMessage: string;
  messageTemplate: string;
  codeLength: number;
  ttlSeconds: string;
}

export const DEFAULT_OTP_CLIENT_VALUES: OtpClientFormValues = {
  name: '',
  company: '',
  contactEmail: '',
  contactPhone: '',
  instanceName: '',
  pricePerMessage: '0.01',
  messageTemplate: 'Your verification code is {code}',
  codeLength: 6,
  ttlSeconds: '300',
};

export type OtpFormError = 'name' | 'email' | 'price' | 'template' | 'ttl';

/** Validates the form and shapes the create/update payload, or names the first bad field. */
export function buildOtpClientPayload(
  v: OtpClientFormValues,
): { ok: true; payload: OtpClientCreate } | { ok: false; error: OtpFormError } {
  const name = v.name.trim();
  if (!name) return { ok: false, error: 'name' };
  const company = v.company.trim();
  const contactEmail = v.contactEmail.trim();
  if (contactEmail && !OTP_EMAIL_RE.test(contactEmail)) return { ok: false, error: 'email' };
  const contactPhone = v.contactPhone.trim();
  const price = v.pricePerMessage.trim();
  // Bounded to DECIMAL(8,4) server-side — reject out-of-range here too (422 not 500).
  if (!OTP_PRICE_RE.test(price) || Number(price) > 9999.9999) return { ok: false, error: 'price' };
  const template = v.messageTemplate.trim();
  if (template.length < 4 || template.length > 500 || !template.includes('{code}')) {
    return { ok: false, error: 'template' };
  }
  const ttl = Number(v.ttlSeconds);
  if (!Number.isInteger(ttl) || ttl < 60 || ttl > 3600) return { ok: false, error: 'ttl' };
  return {
    ok: true,
    payload: {
      name,
      // Empty → null so an edit clears a previously set value (mirrors instanceName).
      company: company ? company : null,
      contactEmail: contactEmail ? contactEmail : null,
      contactPhone: contactPhone ? contactPhone : null,
      instanceName: v.instanceName ? v.instanceName : null,
      messageTemplate: template,
      codeLength: v.codeLength,
      ttlSeconds: ttl,
      pricePerMessage: price,
    },
  };
}

/** Translated toast message for a {@link buildOtpClientPayload} failure. */
export function useOtpFormErrorMessage(): (err: OtpFormError) => string {
  const { t } = useTranslation('platform');
  return useCallback(
    (err: OtpFormError): string => {
      switch (err) {
        case 'name':
          return t('otpService.form.nameRequired', { defaultValue: 'Enter a client name.' });
        case 'email':
          return t('otpService.form.contactEmailInvalid', {
            defaultValue: 'Enter a valid contact email, e.g. name@example.com.',
          });
        case 'price':
          return t('otpService.form.priceInvalid', {
            defaultValue: 'Enter a valid price, e.g. 0.01 — digits with up to 4 decimals.',
          });
        case 'template':
          return t('otpService.form.templateInvalid', {
            defaultValue: 'The template must contain {code} and be 4–500 characters.',
          });
        case 'ttl':
          return t('otpService.form.ttlInvalid', {
            defaultValue: 'Code lifetime must be between 60 and 3600 seconds.',
          });
      }
    },
    [t],
  );
}

export interface OtpClientFormProps {
  values: OtpClientFormValues;
  onChange: (patch: Partial<OtpClientFormValues>) => void;
  /** Gateway instances for the picker; null while loading. */
  instances: WaInstance[] | null;
  /** Unique id prefix so the create + edit dialogs never collide. */
  idPrefix: string;
}

/**
 * Shared field set for the create + edit OTP-client dialogs. Pure controlled
 * component — the owning dialog holds the values and submits via
 * {@link buildOtpClientPayload}.
 */
export function OtpClientForm({ values, onChange, instances, idPrefix }: OtpClientFormProps): JSX.Element {
  const { t } = useTranslation('platform');

  const selected = values.instanceName
    ? instances?.find((i) => i.name === values.instanceName)
    : undefined;
  // Set on the client but gone from the gateway (deleted instance).
  const gone = Boolean(values.instanceName) && instances !== null && !selected;

  const instanceHint = gone ? (
    <span className="text-warning">
      {t('otpService.form.instanceGone', {
        defaultValue: 'This instance no longer exists on the gateway — sends will fail.',
      })}
    </span>
  ) : selected && selected.state !== 'open' ? (
    <span className="text-warning">
      {t('otpService.form.instanceNotOpen', {
        defaultValue: 'This instance is not connected — sends will fail until it is.',
      })}
    </span>
  ) : (
    t('otpService.form.instanceHint', {
      defaultValue: "The WhatsApp login that delivers this client's codes.",
    })
  );

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <FormField
        id={`${idPrefix}-name`}
        label={t('otpService.form.name', { defaultValue: 'Client name' })}
        required
      >
        <Input
          id={`${idPrefix}-name`}
          value={values.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={t('otpService.form.namePlaceholder', { defaultValue: 'Acme Apps' })}
          maxLength={120}
        />
      </FormField>

      <FormField
        id={`${idPrefix}-company`}
        label={t('otpService.form.company', { defaultValue: 'Company' })}
      >
        <Input
          id={`${idPrefix}-company`}
          value={values.company}
          onChange={(e) => onChange({ company: e.target.value })}
          placeholder={t('otpService.form.companyPlaceholder', { defaultValue: 'Acme Inc.' })}
          maxLength={160}
        />
      </FormField>

      <FormField
        id={`${idPrefix}-contact-email`}
        label={t('otpService.form.contactEmail', { defaultValue: 'Contact email' })}
      >
        <Input
          id={`${idPrefix}-contact-email`}
          type="email"
          value={values.contactEmail}
          onChange={(e) => onChange({ contactEmail: e.target.value })}
          placeholder="name@example.com"
          dir="ltr"
          maxLength={191}
        />
      </FormField>

      <FormField
        id={`${idPrefix}-contact-phone`}
        label={t('otpService.form.contactPhone', { defaultValue: 'Contact phone' })}
      >
        <Input
          id={`${idPrefix}-contact-phone`}
          value={values.contactPhone}
          onChange={(e) => onChange({ contactPhone: e.target.value })}
          placeholder="+964 750 000 0000"
          dir="ltr"
          maxLength={32}
        />
      </FormField>

      <FormField
        id={`${idPrefix}-instance`}
        label={t('otpService.form.instance', { defaultValue: 'WhatsApp instance' })}
        helperText={instanceHint}
      >
        <select
          id={`${idPrefix}-instance`}
          className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
          value={values.instanceName}
          onChange={(e) => onChange({ instanceName: e.target.value })}
        >
          <option value="">{t('otpService.form.instanceNone', { defaultValue: '— none —' })}</option>
          {gone && <option value={values.instanceName}>{values.instanceName}</option>}
          {(instances ?? []).map((i) => (
            <option key={i.name} value={i.name}>
              {`${i.name} — ${t(`whatsapp.state.${i.state}`, { defaultValue: i.state })}`}
            </option>
          ))}
        </select>
      </FormField>

      <FormField
        id={`${idPrefix}-price`}
        label={t('otpService.form.price', { defaultValue: 'Price per message (USD)' })}
        helperText={t('otpService.form.priceHint', { defaultValue: 'USD per message. 0 = free.' })}
      >
        <Input
          id={`${idPrefix}-price`}
          value={values.pricePerMessage}
          onChange={(e) => onChange({ pricePerMessage: e.target.value })}
          inputMode="decimal"
          dir="ltr"
          className="font-mono"
          placeholder="0.01"
        />
      </FormField>

      <FormField
        id={`${idPrefix}-code-length`}
        label={t('otpService.form.codeLength', { defaultValue: 'Code length' })}
      >
        <select
          id={`${idPrefix}-code-length`}
          className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
          value={values.codeLength}
          onChange={(e) => onChange({ codeLength: Number(e.target.value) })}
        >
          {CODE_LENGTHS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </FormField>

      <FormField
        id={`${idPrefix}-ttl`}
        label={t('otpService.form.ttl', { defaultValue: 'Code lifetime (seconds)' })}
        helperText={t('otpService.form.ttlHint', {
          defaultValue: '60–3600 seconds. Default 300 (5 minutes).',
        })}
      >
        <Input
          id={`${idPrefix}-ttl`}
          type="number"
          min={60}
          max={3600}
          value={values.ttlSeconds}
          onChange={(e) => onChange({ ttlSeconds: e.target.value })}
          dir="ltr"
          className="font-mono"
        />
      </FormField>

      <div className="md:col-span-2">
        <FormField
          id={`${idPrefix}-template`}
          label={t('otpService.form.template', { defaultValue: 'Message template' })}
          helperText={t('otpService.form.templateHint', {
            defaultValue: 'Must contain the {code} placeholder — it is replaced with the generated code.',
          })}
        >
          <textarea
            id={`${idPrefix}-template`}
            className="block min-h-[60px] w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            value={values.messageTemplate}
            onChange={(e) => onChange({ messageTemplate: e.target.value })}
            maxLength={500}
          />
        </FormField>
      </div>
    </div>
  );
}
